import useSharedRecord from "../hooks/useSharedRecord";
import { savedCandidatePhoto } from "./candidate/CandidatePhoto";
import { formatPhone } from "../utils/formatPhone";
import { candidateName } from "../utils/candidateName";
import { candidateExportRows } from "../utils/candidateExport";
import PageHeading from "../components/PageHeading";
import { createListSearch, normalizeSearch } from "../utils/listSearch";
import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Users, UserPlus, Search, Loader2, Mail, Phone, FileText, Download, Clock } from "lucide-react";
import axios from "../axios/AxiosInstance";
import { message, Modal } from "antd";
import CreateCandidateModal from "./candidate/CreateCandidateModal";
import { CandidateModal } from "./candidate/CandidateModal";

export default function CandidatsPage() {
  const [recordsReady, setRecordsReady] = useState(false);
  const [originalCandidates, setOriginalCandidates] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const loadingSearch = false;
  const [isAddCandidate, setIsAddCandidate] = useState(false);
  const [isViewCandidate, setIsViewCandidate] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const {select: selectSharedCandidate, missing: missingSharedCandidate} = useSharedRecord(originalCandidates, recordsReady, setSelectedCandidate);
  useEffect(() => {setIsViewCandidate(Boolean(selectedCandidate));}, [selectedCandidate]);


  const [cv, setCv] = useState(null);
  const [cvBusy, setCvBusy] = useState(null);
  useEffect(() => () => { if (cv?.url) URL.revokeObjectURL(cv.url); }, [cv]);
  const viewCv = async candidate => {
    if (cvBusy) return;
    setCvBusy(candidate._id);
    try {
      const {data} = await axios.get(`cv/${candidate._id}`);
      if (!data?.data) throw new Error();
      const raw = atob(data.data.replace(/^data:[^,]+,/, ""));
      const blob = new Blob([Uint8Array.from(raw, char => char.charCodeAt(0))], {type: "application/pdf"});
      setCv({name: candidateName(candidate), url: URL.createObjectURL(blob)});
    } catch { message.error("CV indisponible pour ce candidat."); }
    finally { setCvBusy(null); }
  };
  const [exportBusy, setExportBusy] = useState(false);
  // Nouveaux états pour le design moderne
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('tous');
    
  // Statistiques des candidats
  const [stats, setStats] = useState({
    total: 0,
    enMission: 0,
  });

  // Fonction pour calculer les statistiques
  const calculateStats = (candidatesList) => {
    const total = candidatesList.length;
    const enMission = candidatesList.filter(c => c.status === 'En mission').length;

    setStats({ total, enMission });
  };

  const search = setSearchQuery;
  const filterByStatus = setStatusFilter;
  const candidateSearch = createListSearch(searchQuery, ["firstname", "lastname", "phone", "mobile", "email", "address", "town", "postCode", "qualification", "qualifications", "qualifications.name", "qualifications.label", "locations", "locations.town", "status", "pld"]);
  const candidates = originalCandidates.filter(candidate =>
    (statusFilter === 'tous' || normalizeSearch(candidate.status) === normalizeSearch(statusFilter)) && candidateSearch(candidate));

  useEffect(() => {
    loadCandidates();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const viewCandidate = (candidate) => {
    selectSharedCandidate(candidate);
  };

  const loadCandidates = () => {
    setIsLoading(true);
    axios
      .get("/tempworker-candidate/freelance")
      .then((res) => {
        setOriginalCandidates(res.data);
        setRecordsReady(true);
        calculateStats(res.data);
        console.log(res.data);
      })
      .catch((e) => {
        console.log(e);
        message.error("Une erreur s'est produite !");
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  // Fonction pour obtenir l'icône de statut


  const exportCandidates = async () => {
    if (exportBusy || !candidates.length) return;
    setExportBusy(true);
    try {
      const XLSX = await import("xlsx");
      const sheet = XLSX.utils.json_to_sheet(candidateExportRows(candidates));
      sheet["!cols"] = [14, 24, 24, 20, 36, 25, 14, 45, 20].map(width => ({wch: width}));
      sheet["!autofilter"] = {ref: sheet["!ref"]};
      const book = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(book, sheet, "Candidats");
      const label = searchQuery.trim().replace(/[^a-zA-Z0-9à-ÿ_-]+/g, "_").slice(0, 50) || "recherche";
      XLSX.writeFile(book, `candidats_${label}.xlsx`);
      message.success(`${candidates.length} candidat(s) exporté(s).`);
    } catch { message.error("Impossible d’exporter les candidats. Réessayez."); }
    finally { setExportBusy(false); }
  };

  return (
    <div className="w-full">
      <div className="w-full">
        {/* Header Section */}
        <motion.div
          className="mb-6"
        >
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center space-x-4">
              <div><PageHeading icon="candidates" title="Candidats" description={<> Gérez votre vivier de talents et identifiez les meilleurs profils </>} />
              </div>
            </div>

            <motion.button
              onClick={() => setIsAddCandidate(true)}
              className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium transition-all duration-200 shadow-lg hover:shadow-xl flex items-center space-x-2"
              whileHover={{ scale: 1.02, y: -2 }}
              whileTap={{ scale: 0.98 }}
            >
              <UserPlus className="w-5 h-5" />
              <span>Nouveau candidat</span>
            </motion.button>
          </div>
        </motion.div>

        {/* Loading Overlay */}
        <AnimatePresence>
          {loadingSearch && (
            <motion.div
              className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
            >
              <motion.div
                className="bg-white rounded-xl p-6 flex items-center space-x-3"
              >
                <Loader2 className="animate-spin h-6 w-6 text-blue-600" />
                <span className="text-slate-700">Recherche en cours...</span>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {cv && <Modal open title={cv ? `CV — ${cv.name}` : "CV"} onCancel={() => setCv(null)} footer={null} width={900}>
          {cv && <iframe src={cv.url} title={`CV de ${cv.name}`} className="h-[70vh] w-full border-0" />}
        </Modal>}
        {/* Modals */}
        {isAddCandidate && (
          <CreateCandidateModal
            open={isAddCandidate}
            candidates={originalCandidates}
            setCandidates={setOriginalCandidates}
            onOpenChange={setIsAddCandidate}
          />
        )}
        {isViewCandidate && selectedCandidate && (
          <CandidateModal
            key={selectedCandidate?._id}
            isOpen={isViewCandidate}
            onClose={() => selectSharedCandidate(null)}
            candidates={originalCandidates}
            setCandidates={setOriginalCandidates}
            candidate={selectedCandidate}
          />
        )}

        {missingSharedCandidate && <p role="alert" className="mb-4 rounded-lg bg-amber-50 p-3 text-sm text-amber-800">Cette fiche candidat est introuvable ou inaccessible dans votre agence.</p>}
        {/* Statistiques */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8"
        >
          <motion.div
            className="bg-white rounded-xl shadow-sm border border-slate-200 p-6"
            whileHover={{ y: -2 }}
          >
            <div className="flex items-center space-x-3">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600">Total candidats</p>
                <p className="text-3xl font-bold text-slate-900">{stats.total}</p>
              </div>
            </div>
          </motion.div>



          <motion.div
            className="bg-white rounded-xl shadow-sm border border-slate-200 p-6"
            whileHover={{ y: -2 }}
          >
            <div className="flex items-center space-x-3">
              <div className="p-3 bg-orange-100 rounded-lg">
                <Clock className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600">En mission</p>
                <p className="text-3xl font-bold text-orange-600">{stats.enMission}</p>
              </div>
            </div>
          </motion.div>


        </motion.div>

        {/* Barre d'outils moderne */}
        <motion.div
          className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-8"
        >
          <div className="flex flex-col lg:flex-row items-center space-y-4 lg:space-y-0 lg:space-x-4">
            {/* Recherche */}
            <div className="relative flex-1 w-full">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-400" />
              <motion.input
                type="search"
                onChange={(e) => search(e.target.value)}
                value={searchQuery}
                placeholder="Nom, téléphone, email, ville, qualification…"
                className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                whileFocus={{ scale: 1.02 }}
              />
            </div>

            <button type="button" disabled={exportBusy || isLoading || !candidates.length} onClick={exportCandidates} className="inline-flex shrink-0 items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-800 hover:bg-emerald-100 disabled:opacity-40"><Download size={17} />{exportBusy ? "Export en cours…" : `Exporter en Excel (${candidates.length})`}</button>
            {/* Filtres */}
            <div className="flex items-center space-x-3">
              <motion.select
                value={statusFilter}
                onChange={(e) => filterByStatus(e.target.value)}
                className="px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                whileFocus={{ scale: 1.02 }}
              >
                <option value="tous">Tous les statuts</option>
                <option value="Disponible">Disponible</option>
                <option value="En mission">En mission</option>
                <option value="Indisponible">Indisponible</option>
              </motion.select>


            </div>
          </div>
        </motion.div>

        {/* Affichage des candidats */}
        {isLoading ? (
          <motion.div
            className="flex justify-center items-center py-20"
          >
            <div className="text-center">
              <Loader2 className="animate-spin h-12 w-12 text-blue-600 mx-auto mb-4" />
              <p className="text-slate-600">Chargement des candidats...</p>
            </div>
          </motion.div>
        ) : (
          <motion.div
          >
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                          Candidat
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                          Contact
                        </th>

                        <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                          Compétences
                        </th>
                        <th scope="col" style={{width: "64px"}}>CV</th>

                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      <AnimatePresence>
                        {candidates.map((candidat) => (
                          <tr
                            key={candidat._id}
                            className="hover:bg-slate-50 cursor-pointer"
                            onClick={() => viewCandidate(candidat)}
                            tabIndex={0}
                            aria-label={`Ouvrir la fiche de ${candidat.firstname} ${candidat.lastname}`}
                            onKeyDown={e => { if (e.target === e.currentTarget && ["Enter", " "].includes(e.key)) {e.preventDefault();viewCandidate(candidat);} }}
                          >
                            <td className="px-6 py-4">
                              <div className="flex items-center space-x-3">
                                <div className="relative">
                                  {savedCandidatePhoto(candidat._id) || candidat.image ? (
                                    <img
                                      src={savedCandidatePhoto(candidat._id) || `data:image/jpeg;base64,${candidat.image}`}
                                      alt={candidateName(candidat)}
                                      className="w-10 h-10 rounded-full object-cover"
                                    />
                                  ) : (
                                    <div className="w-10 h-10 rounded-full bg-gradient-to-r from-blue-400 to-blue-600 flex items-center justify-center text-white text-sm font-bold">
                                      {candidat.firstname.charAt(0)}{candidat.lastname.charAt(0)}
                                    </div>
                                  )}
                                </div>
                                <div>
                                  <p className="text-sm font-medium text-slate-900">
                                    {candidateName(candidat)}
                                  </p>
                                  {candidat.pld && (
                                    <p className="text-xs text-red-600">#{candidat.pld}</p>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="space-y-1">
                                <div className="flex items-center space-x-2 text-sm text-slate-600">
                                  <Mail className="w-3 h-3" />
                                  <span className="truncate max-w-48">{candidat.email}</span>
                                </div>
                                {candidat.phone && (
                                  <div className="flex items-center space-x-2 text-sm text-slate-600">
                                    <Phone className="w-3 h-3" />
                                    <span>{formatPhone(candidat.phone)}</span>
                                  </div>
                                )}
                              </div>
                            </td>

                            <td className="px-6 py-4">
                              <div className="flex flex-wrap gap-1 max-w-48">
                                {candidat.qualifications?.slice(0, 2).map((comp, index) => (
                                  <span
                                    key={index}
                                    className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded-full"
                                  >
                                    {comp}
                                  </span>
                                ))}
                                {candidat.qualifications && candidat.qualifications.length > 2 && (
                                  <span className="px-2 py-1 text-xs bg-slate-100 text-slate-600 rounded-full">
                                    +{candidat.qualifications.length - 2}
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="text-center align-middle"><button type="button" title="Voir le CV" aria-label={`Voir le CV de ${candidat.firstname} ${candidat.lastname}`} disabled={Boolean(cvBusy)} className="inline-flex shrink-0 items-center justify-center rounded-lg p-2 text-blue-800 hover:bg-blue-50 disabled:opacity-50" onClick={e => {e.stopPropagation();viewCv(candidat);}}>{cvBusy === candidat._id ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}</button></td>

                          </tr>
                        ))}
                      </AnimatePresence>
                    </tbody>
                  </table>
                </div>
              </div>

            {/* Message si aucun candidat */}
            {candidates.length === 0 && !isLoading && (
              <motion.div
                className="text-center py-20"
              >
                <div className="text-slate-400 mb-4">
                  <Users className="w-16 h-16 mx-auto" />
                </div>
                <h3 className="text-lg font-medium text-slate-900 mb-2">Aucun candidat trouvé</h3>
                <p className="text-slate-600 mb-6">
                  {searchQuery ? 'Aucun candidat ne correspond à votre recherche.' : 'Commencez par ajouter votre premier candidat.'}
                </p>
                {!searchQuery && (
                  <motion.button
                    onClick={() => setIsAddCandidate(true)}
                    className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium transition-colors duration-200"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Ajouter un candidat
                  </motion.button>
                )}
              </motion.div>
            )}
          </motion.div>
        )}
      </div>
    </div>
  );
}
